// Staff-side invoice calls for one matter. Deliberately no fixture
// fallback: real rows or an honest error.

import { downloadStaffFile, getAuthHeaders } from "../../../api/auth";

export interface CaseInvoice {
  _id: string;
  number?: string | null;
  description: string;
  amountCents: number;
  currency?: string;
  dueDate?: string | null;
  status: "open" | "paid" | "void";
  notifiedAt?: string | null;
  createdAt?: string;
}

export interface CreateInvoiceInput {
  amountCents: number;
  description: string;
  dueDate?: string;
  notifyClient: boolean;
}

export async function fetchCaseInvoices(caseId: string): Promise<CaseInvoice[]> {
  const res = await fetch(`/api/cases/${caseId}/invoices`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET invoices failed: ${res.status}`);
  const body = (await res.json()) as { invoices: CaseInvoice[] };
  return body.invoices;
}

export async function createInvoice(
  caseId: string,
  input: CreateInvoiceInput,
): Promise<{ invoice: CaseInvoice; emailSent: boolean }> {
  const res = await fetch(`/api/cases/${caseId}/invoices`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(input),
  });
  const body = (await res.json().catch(() => ({}))) as {
    invoice?: CaseInvoice;
    emailSent?: boolean;
    error?: string;
  };
  if (!res.ok || !body.invoice) {
    throw new Error(body.error ?? `Create invoice failed: ${res.status}`);
  }
  return { invoice: body.invoice, emailSent: !!body.emailSent };
}

export async function downloadInvoicePdf(invoiceId: string, number?: string | null): Promise<void> {
  return downloadStaffFile(`/api/invoices/${invoiceId}/pdf`, `${number ?? "invoice"}.pdf`);
}

export async function voidInvoice(invoiceId: string): Promise<void> {
  const res = await fetch(`/api/invoices/${invoiceId}/void`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Void failed: ${res.status}`);
  }
}

export interface RecordPaymentInput {
  method: "cash" | "check" | "zelle" | "other";
  reference?: string;
  note?: string;
  /** A date (yyyy-mm-dd), never a browser timestamp - the server rejects
   * the future, and clock skew would make "now" flaky. */
  paidAt?: string;
}

/** Admin-only: record an offline payment the office took by hand. */
export async function recordManualPayment(
  invoiceId: string,
  input: RecordPaymentInput,
): Promise<{ healed: boolean }> {
  const res = await fetch(`/api/invoices/${invoiceId}/payments`, {
    method: "POST",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Recording failed (${res.status})`);
  }
  return (await res.json()) as { healed: boolean };
}
